import { Injectable } from '@angular/core';

/** Duración del vuelo. Igual que la del minimizado de tratamiento. */
export const VUELO_DESCARGAS_MS = 420;

/** Selector del botón de Descargas de la barra superior (el destino). */
const ANCLA = '[data-descargas-anchor]';

/**
 * Animación "esto se fue a Descargas": el diálogo (o el botón que se tocó) vuela
 * hasta el ícono de Descargas de la barra superior, que después pulsa.
 *
 * Es el análogo del minimizado de tratamiento (`tratamiento-minimizado.service`),
 * pero acá el destino no es una esquina fija: se calcula contra el botón real,
 * así queda bien en cualquier tamaño de ventana. Si el usuario pidió menos
 * movimiento (`prefers-reduced-motion`) o no hay ancla, no anima nada.
 */
@Injectable({ providedIn: 'root' })
export class VueloDescargasService {
  private get animacionesReducidas(): boolean {
    return (
      typeof matchMedia === 'function' &&
      matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  private get ancla(): HTMLElement | null {
    return document.querySelector<HTMLElement>(ANCLA);
  }

  /**
   * Vuela `panel` (el `.cdk-overlay-pane` del diálogo) hacia el botón de
   * Descargas. Devuelve los ms que hay que esperar antes de cerrar el diálogo
   * (0 si no se animó, para no demorar la acción de gusto).
   */
  public volarPanel(panel?: HTMLElement | null): number {
    const ancla = this.ancla;
    if (!panel || !ancla || this.animacionesReducidas) {
      this.pulsarAncla();
      return 0;
    }

    const desde = panel.getBoundingClientRect();
    const hasta = ancla.getBoundingClientRect();
    const dx =
      hasta.left + hasta.width / 2 - (desde.left + desde.width / 2);
    const dy = hasta.top + hasta.height / 2 - (desde.top + desde.height / 2);

    panel.style.pointerEvents = 'none';
    panel.animate(
      [
        { transform: 'translate(0, 0) scale(1)', opacity: 1 },
        {
          transform: `translate(${dx}px, ${dy}px) scale(0.06)`,
          opacity: 0,
        },
      ],
      { duration: VUELO_DESCARGAS_MS, easing: 'cubic-bezier(0.4, 0, 0.6, 1)' },
    );

    // El backdrop se va con el panel: si se queda, la pantalla parece trabada.
    const backdrop = document.querySelector<HTMLElement>(
      '.cdk-overlay-backdrop-showing',
    );
    backdrop?.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: VUELO_DESCARGAS_MS,
      easing: 'ease-out',
      fill: 'forwards',
    });

    setTimeout(() => this.pulsarAncla(), VUELO_DESCARGAS_MS - 80);
    return VUELO_DESCARGAS_MS;
  }

  /** El ícono de Descargas "recibe" el archivo: un pulso corto. */
  private pulsarAncla(): void {
    const ancla = this.ancla;
    if (!ancla || this.animacionesReducidas) return;
    ancla.animate(
      [
        { transform: 'scale(1)' },
        { transform: 'scale(1.35)' },
        { transform: 'scale(1)' },
      ],
      { duration: 460, easing: 'ease-out' },
    );
  }
}
